import debounce from 'lodash.debounce'
import React from 'react'
import ReactDOM from 'react-dom'
import FocusLock from 'react-focus-lock'

import { useI18n } from '../../../configuration/contexts/I18nContext'
import styled, { getFontSize, MQ_TABLET } from '../../constants/theme'
import { CirclePlusIcon } from '../elements/Icons'

export enum ModalStyle {
  DARK = 'dark',
  LIGHT = 'light',
  LIGHT_OVERLAY = 'light-overlay',
}

interface Props {
  autoFocus?: boolean
  children: React.ReactNode
  fitToWindow?: boolean
  hideCloseButton?: boolean
  isOpen: boolean
  onRequestClose?: (e: KeyboardEvent | MouseEvent | boolean) => void
  style?: ModalStyle
}

export const getModalContainer = () => {
  const CONTAINER_ID = 'ModalContainer'
  if (!document.getElementById(CONTAINER_ID)) {
    const div = document.createElement('div')
    div.setAttribute('id', CONTAINER_ID)
    document.body.appendChild(div)
  }
  return document.getElementById(CONTAINER_ID)
}

const Modal: React.FC<Props> = (props) => {
  const {
    autoFocus,
    children,
    fitToWindow,
    hideCloseButton,
    isOpen,
    onRequestClose,
    style,
  } = props
  const containerEl = React.useMemo(getModalContainer, [])
  const contentEl = React.useRef<HTMLDivElement | null>(null)
  const modalEl = React.useRef<HTMLDivElement | null>(null)
  const i18n = useI18n()

  const closeModal = React.useCallback(
    (e) => {
      e.stopPropagation()
      onRequestClose?.(e)
    },
    [onRequestClose],
  )

  const calculateFitWidth = React.useCallback(() => {
    if (contentEl.current) {
      contentEl.current.style.width = 'auto'
      const cw = contentEl.current.scrollWidth
      const ch = contentEl.current.scrollHeight
      const cr = cw / ch // content ratio
      const aw = contentEl.current.offsetWidth
      const ah = contentEl.current.offsetHeight
      const ar = aw / ah // available ratio
      const { value } = getFontSize()
      const newWidth = ar > cr ? cw * (ah / ch) : aw * (aw / cw)
      const increase = newWidth * (value * 0.1875)
      contentEl.current.style.width = `${Math.ceil(newWidth - increase)}px`
    }
  }, [])

  const handleClickCloseModal = React.useCallback(
    (e) => {
      const classList = e.target.classList

      if (classList.contains('ModalOverlay') && hideCloseButton) {
        return
      }

      classList.contains('CloseModal') && closeModal(e)
    },
    [closeModal, hideCloseButton],
  )

  const handleKeyDown = React.useCallback(
    (e) => {
      const isEscape =
        (e.key || '').toLowerCase() === 'escape' ||
        e.keyCode === 27 ||
        e.which === 27

      if (isEscape) {
        if (hideCloseButton) {
          return
        }

        closeModal(e)
      }
    },
    [closeModal, hideCloseButton],
  )

  const handleResize = React.useMemo(
    () => debounce(calculateFitWidth, 333),
    [calculateFitWidth],
  )

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    if (fitToWindow) {
      window.addEventListener('resize', handleResize)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleResize)
    }
  }, [fitToWindow, handleKeyDown, handleResize])

  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open')
      if (fitToWindow && contentEl.current) {
        const img = contentEl.current.querySelector('img')
        if (img) {
          img.onload = () => {
            calculateFitWidth()
          }
        }
      }
      requestAnimationFrame(() => {
        if (modalEl.current) {
          modalEl.current.style.opacity = '1'
        }
      })
    } else {
      document.body.classList.remove('modal-open')
    }

    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [calculateFitWidth, fitToWindow, isOpen])

  // Only add `autoFocus` to `<FocusLock />` if defined.
  const focusLockProps: { autoFocus?: boolean } = {}
  if (typeof autoFocus === 'boolean') {
    focusLockProps.autoFocus = autoFocus
  }

  return isOpen && containerEl
    ? ReactDOM.createPortal(
        <FocusLock {...focusLockProps} returnFocus>
          <ModalOverlay
            className={`CloseModal ModalOverlay ${style || ModalStyle.DARK}`}
            ref={modalEl}
            key="modal"
            onClick={handleClickCloseModal}
            role="dialog"
            title="Modal overlay"
          >
            <ModalContents fitToWindow={fitToWindow} ref={contentEl}>
              {children}
              {hideCloseButton ? undefined : (
                <ModalCloseButton
                  className="CloseModal"
                  onClick={closeModal}
                  title={i18n.t('closeModal')}
                >
                  <CirclePlusIcon />
                </ModalCloseButton>
              )}
            </ModalContents>
          </ModalOverlay>
        </FocusLock>,
        containerEl,
      )
    : null
}

export const ModalOverlay = styled.div(
  {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    bottom: 0,
    display: 'flex',
    left: 0,
    opacity: 0,
    overflowY: 'scroll',
    position: 'fixed',
    right: 0,
    top: 0,
    transition: 'opacity .333s ease',
    zIndex: 5000,
    '&.light-overlay': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
  },
  ({ theme }) => ({
    ...MQ_TABLET({
      padding: theme.padding,
    }),
    padding: theme.paddingMobile,
  }),
)

interface ContentProps {
  fitToWindow?: boolean
}

const ModalContents = styled.div<ContentProps>(
  {
    margin: 'auto',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  ({ fitToWindow, theme }) => ({
    backgroundColor: theme.background,
    borderRadius: theme.borderRadius,
    maxHeight: fitToWindow ? '100%' : 'none',
  }),
)

export const ModalCloseButton = styled.button(({ theme }) => ({
  borderRadius: theme.borderRadiusPillButton,
  height: 37,
  marginRight: theme.paddingSmall,
  marginTop: theme.paddingSmall,
  padding: 3,
  position: 'absolute',
  right: 0,
  top: 0,
  width: 37,
  zIndex: 10,
  '&:focus svg': {
    boxShadow: `0 0 0 1px ${theme.background}, 0 0 0 3px ${theme.primary}`,
  },

  '.dark &:focus svg': {
    boxShadow: `0 0 0 1px ${theme.background}, 0 0 0 3px ${theme.textLight}`,
  },

  '& svg': {
    '& path': {
      stroke: theme.background,
    },
    '.light &': {
      background: theme.background,
      borderColor: theme.background,
      fill: theme.text,
    },
    borderRadius: 15,
    height: 24,
    transform: 'rotate(45deg)',
    width: 24,
  },
}))

export default React.memo(Modal)
